/**
 * 智慧調整 schema(REQ-20260523):
 * GPT-5.5 解析 admin 自然語言 → 套用 → 還原(時光回朔機)。
 */
import { z } from 'zod';

export const SMART_ADJUST_TIERS = ['A', 'B', 'C'] as const;
export type SmartAdjustTier = (typeof SMART_ADJUST_TIERS)[number];

/**
 * 進度條的 6 階段 — UI Stepper 顯示用。
 * - analyzing:LLM 解析需求 + (C 級)AI-agent dryRun 在跑
 * - backup:apply 開始前拍 mysqldump
 * - coding:實際套用變更 — A/B = DB 寫入(快),C = AI-agent spawn 寫程式碼(慢)
 * - verifying:C = git commit 完成後的驗證緩衝(後續可接 typecheck)
 * - deploying:C = git push → webhook 觸發 redeploy 中
 * - done:全部完成
 */
export const SMART_ADJUST_PHASES = [
  'analyzing',
  'backup',
  'coding',
  'verifying',
  'deploying',
  'done',
] as const;
export type SmartAdjustPhase = (typeof SMART_ADJUST_PHASES)[number];

export const SMART_ADJUST_STATUSES = [
  /** 剛建立,AI 分析還在背景跑(BullMQ queue,concurrency=1 排隊)*/
  'analyzing',
  /** 分析完成,plan 已寫入,等使用者按「開始升級」*/
  'planned',
  /** 使用者已按「開始升級」,實際 apply(備份 + DB 寫入 / AI-agent commit + push)背景跑中 */
  'applying',
  /** 已套用 */
  'applied',
  /** 失敗(背景分析錯誤、apply 錯誤、API 重啟、…)*/
  'failed',
  /** 使用者主動中斷 — 跟 failed 區分:failed 是系統判定失敗,cancelled 是 user 拒收 */
  'cancelled',
  /** 已還原 */
  'rolledback',
] as const;
export type SmartAdjustStatus = (typeof SMART_ADJUST_STATUSES)[number];

/** B 級可動的表(其他 system_* / change_snapshots / users / audit_logs 等不准動)*/
export const SMART_ADJUST_B_TABLES = [
  'customers',
  'customer_phones',
  'suppliers',
  'supplier_phones',
  'products',
  'interviews',
  'inventory_records',
  'ar_settlements',
  'ar_settlement_lines',
  'ap_settlements',
  'ap_settlement_lines',
  'sales_orders',
  'sales_order_lines',
  'purchase_orders',
  'purchase_order_lines',
  'product_stocks',
  'stock_transactions',
] as const;
export type SmartAdjustBTable = (typeof SMART_ADJUST_B_TABLES)[number];

// ─── A 級 plan(系統設定變更)───────────────────────────────

/**
 * 單一 SystemSetting key 的變更:
 * - key:system_setting 的 row key(白名單內)
 * - beforeValue / afterValue:整個 JSON value(因為 SystemSetting.value 是 JSON,
 *   反向 restore 直接 SET value = beforeValue)
 */
export const SmartAdjustSettingActionSchema = z.object({
  key: z.string().min(1).max(50),
  beforeValue: z.unknown(),
  afterValue: z.unknown(),
  /** 該變更的人話描述,顯示在 UI 預覽 */
  describe: z.string().max(500),
});
export type SmartAdjustSettingAction = z.infer<typeof SmartAdjustSettingActionSchema>;

/** 白話說明:給非工程使用者看,絕對不可帶路徑 / SQL / 欄位名等技術術語 */
const userExplanationField = z.string().trim().min(1).max(800).optional();

/**
 * 還原難度:user 拍板前必須知道升級後想反悔的代價
 * - easy:   反向 UPDATE / setting 改回前值,30 秒內 done(A 級典型)
 * - medium: git revert + 程式碼 redeploy 5-10 分鐘,或 prisma row 還原(B / C 純 code)
 * - hard:   涉及 schema 變動 — git revert 還原 code,但 DB 內新加欄位 / 表會留下
 *           要清乾淨需工程手動 down migration(C 級含 schema)
 */
export const SMART_ADJUST_RESTORE_COMPLEXITIES = ['easy', 'medium', 'hard'] as const;
export type SmartAdjustRestoreComplexity = (typeof SMART_ADJUST_RESTORE_COMPLEXITIES)[number];
const restoreComplexityField = z.enum(SMART_ADJUST_RESTORE_COMPLEXITIES).optional();

export const SmartAdjustPlanASchema = z.object({
  tier: z.literal('A'),
  actions: z.array(SmartAdjustSettingActionSchema).min(1).max(20),
  userExplanation: userExplanationField,
  restoreComplexity: restoreComplexityField,
});
export type SmartAdjustPlanA = z.infer<typeof SmartAdjustPlanASchema>;

// ─── B 級 plan(業務資料批改)─────────────────────────────

/**
 * 單一 op:
 *   UPDATE — where + set 必填,where 不可為空(禁 mass update)
 *   DELETE — where 必填,where 不可為空(禁 mass delete)
 *
 * where / set 直接傳 Prisma 語法的 plain object,後端轉成 prisma[table].updateMany 等。
 * GPT 應該輸出 Prisma 風格(例 `{ contact: { startsWith: '陳' } }`);
 * 後端會嘗試直接傳給 Prisma,失敗會 fall back 到 REJECT。
 */
export const SmartAdjustOpUpdateSchema = z.object({
  type: z.literal('UPDATE'),
  table: z.enum(SMART_ADJUST_B_TABLES),
  where: z.record(z.unknown()),
  set: z.record(z.unknown()),
  describe: z.string().max(500),
});
export type SmartAdjustOpUpdate = z.infer<typeof SmartAdjustOpUpdateSchema>;

export const SmartAdjustOpDeleteSchema = z.object({
  type: z.literal('DELETE'),
  table: z.enum(SMART_ADJUST_B_TABLES),
  where: z.record(z.unknown()),
  describe: z.string().max(500),
});
export type SmartAdjustOpDelete = z.infer<typeof SmartAdjustOpDeleteSchema>;

export const SmartAdjustOpSchema = z.union([
  SmartAdjustOpUpdateSchema,
  SmartAdjustOpDeleteSchema,
]);
export type SmartAdjustOp = z.infer<typeof SmartAdjustOpSchema>;

export const SmartAdjustPlanBSchema = z.object({
  tier: z.literal('B'),
  operations: z.array(SmartAdjustOpSchema).min(1).max(10),
  userExplanation: userExplanationField,
  restoreComplexity: restoreComplexityField,
});
export type SmartAdjustPlanB = z.infer<typeof SmartAdjustPlanBSchema>;

/** B 級 dry-run 預覽結果(每個 op 受影響的列數 + 前 5 列 sample)*/
export const SmartAdjustBDryRunResultSchema = z.object({
  perOp: z.array(
    z.object({
      describe: z.string(),
      table: z.string(),
      affectedCount: z.number().int(),
      sample: z.array(z.unknown()).max(5),
    }),
  ),
});
export type SmartAdjustBDryRunResult = z.infer<typeof SmartAdjustBDryRunResultSchema>;

// ─── C 級 plan(code 變動,via AI-agent)────────────────

/**
 * C 級限制(V1):**只能改 code,不能改 schema**。
 * AI-agent 端 prompt 會 prefix 安全規則,GPT 也應該在 plan 階段就拒絕
 * 涉及 schema.prisma / migration 的請求。
 */
export const SmartAdjustPlanCSchema = z.object({
  tier: z.literal('C'),
  /**
   * 給執行體(主機 Claude Code)的 prompt。
   *
   * 100000 而非原本的 8000:8000 對「跨多個模組、要列出既有決策」的計畫太緊,
   * 超過就會被 zod 擋成 REJECT —— 而那個失敗看起來像「AI 不會做」,
   * 實際上是計畫寫太詳細。
   *
   * 上限要跟 AI-agent 的 `MAX_PROMPT_CHARS`(200000)一起看:
   * 後端還會在這段後面附加「建立 REQ 文件」的要求(含需求原文),
   * 兩者相加不能超過那個值,所以這裡只取一半。
   */
  codexPrompt: z.string().min(1).max(100_000),
  /** Git commit message */
  commitMessage: z.string().min(1).max(200),
  /** 預期會改的檔案 path 提示(可空,只給 UI 顯示)*/
  expectedFiles: z.array(z.string().max(255)).max(50).default([]),
  /** 人話描述這次改了什麼,給 UI / audit 用 */
  describe: z.string().max(500),
  userExplanation: userExplanationField,
  restoreComplexity: restoreComplexityField,
  /** 是否涉及 schema 變動(由 GPT 自我宣告;UI 顯示警告 + restore complexity=hard)*/
  involvesSchemaChange: z.boolean().optional(),
});
export type SmartAdjustPlanC = z.infer<typeof SmartAdjustPlanCSchema>;

// ─── reject(GPT 拒絕、或 tier 超出 MVP)─────────────────────

export const SmartAdjustPlanRejectedSchema = z.object({
  tier: z.literal('REJECT'),
  // 3000 而非 500:拒絕原因可能是驗證關卡列出的新增型別 / lint 問題清單,
  // 或 AI-agent 的最後輸出摘要(既有程式碼已有 slice(0, 3000) 的用法)。
  // 截太短會只剩結論、看不到證據,使用者無從判斷該怎麼改需求。
  reason: z.string().max(3000),
});
export type SmartAdjustPlanRejected = z.infer<typeof SmartAdjustPlanRejectedSchema>;

// ─── 統一 plan ────────────────────────────────────────────

export const SmartAdjustPlanSchema = z.union([
  SmartAdjustPlanASchema,
  SmartAdjustPlanBSchema,
  SmartAdjustPlanCSchema,
  SmartAdjustPlanRejectedSchema,
]);
export type SmartAdjustPlan = z.infer<typeof SmartAdjustPlanSchema>;

// ─── REST API I/O ─────────────────────────────────────────

/**
 * 單一附件(圖片 / 文字檔)。前端走 multipart 上傳,後端落三個地方:
 *  - `key`:MinIO 物件 key,系統自己的留存
 *  - `workspacePath`:主機工作區的**相對**路徑。分析執行體是主機上的 Claude Code
 *    (cwd = repo 根目錄,只有 Read/Grep/Glob),唯有落在工作區的檔案它讀得到。
 *  - Redmine 工單附件由 `RedmineService` 另外掛,不在此結構內
 */
export const SmartAdjustAttachmentSchema = z.object({
  key: z.string().max(500),
  filename: z.string().max(255),
  workspacePath: z.string().max(500),
});
export type SmartAdjustAttachment = z.infer<typeof SmartAdjustAttachmentSchema>;

export const SmartAdjustPlanRequestSchema = z.object({
  request: z.string().trim().min(1, '請描述要做什麼').max(2000),
  /** 預期行為 — 期待完成後的樣子 */
  expectedBehavior: z.string().trim().max(2000).optional(),
  /** 限制條件 — 不要動到什麼 / 一定要保留什麼 */
  constraints: z.string().trim().max(2000).optional(),
  /** 附件清單(僅 plan API 內部用,前端 form-data 上傳後由後端填)*/
  attachments: z.array(SmartAdjustAttachmentSchema).max(10).optional(),
});
export type SmartAdjustPlanRequest = z.infer<typeof SmartAdjustPlanRequestSchema>;

// ─── 整合設定(Redmine / GitLab)──────────────────────────

export const SmartAdjustRedmineConfigSchema = z.object({
  url: z.string().trim().url().or(z.literal('')).default(''),
  projectIdentifier: z.string().trim().max(100).default(''),
  /** API key — 寫入時可填新值,GET 回傳會 mask 成 '[ENCRYPTED]' */
  apiKey: z.string().max(500).default(''),
});
export type SmartAdjustRedmineConfig = z.infer<typeof SmartAdjustRedmineConfigSchema>;

export const SmartAdjustGitlabConfigSchema = z.object({
  url: z.string().trim().url().or(z.literal('')).default(''),
  /** Personal Access Token — 寫入時可填新值,GET 時 mask;V2 push / 建 issue 用 */
  accessToken: z.string().max(500).default(''),
});
export type SmartAdjustGitlabConfig = z.infer<typeof SmartAdjustGitlabConfigSchema>;

// ─── 主機 Claude Code 執行體設定(REQ-20260802001)─────────

/**
 * 模型只收**別名**不收完整 id(如 `claude-opus-5`):別名會自動跟上該系列最新
 * 模型,寫死完整 id 的話出新版就過期,而過期只會在真的送出升級時才炸。
 * `''` = 不帶 `--model`,用主機 Claude Code 自己的預設(= 本設定加入前的行為)。
 */
export const SmartAdjustClaudeModelSchema = z.enum(['', 'opus', 'sonnet', 'haiku']);

/** 對應 `claude --effort <level>`;`''` = 不帶該旗標 */
export const SmartAdjustClaudeEffortSchema = z.enum([
  '',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
]);

export const SmartAdjustClaudeRunConfigSchema = z.object({
  model: SmartAdjustClaudeModelSchema.default(''),
  effort: SmartAdjustClaudeEffortSchema.default(''),
});
export type SmartAdjustClaudeRunConfig = z.infer<typeof SmartAdjustClaudeRunConfigSchema>;

/**
 * 分析與改 code 分開設定:分析多半是讀檔 + 判斷,改 code 才需要強推理。
 * 綁在一起的話,想讓改 code 用 opus + high 就得連帶讓每次分析也燒同樣的額度。
 */
export const SmartAdjustClaudeConfigSchema = z.object({
  /** `POST /analyze` — 唯讀產出計畫 JSON */
  analyze: SmartAdjustClaudeRunConfigSchema.default({}),
  /** `POST /run` — C 級 dry-run 與實際改 code(兩者用同一組,本來就是同一件事跑兩次)*/
  edit: SmartAdjustClaudeRunConfigSchema.default({}),
});
export type SmartAdjustClaudeConfig = z.infer<typeof SmartAdjustClaudeConfigSchema>;

export const SmartAdjustIntegrationsSchema = z.object({
  redmine: SmartAdjustRedmineConfigSchema,
  gitlab: SmartAdjustGitlabConfigSchema,
  /** 舊 row 沒有這個 key → 走 default,行為等同未設定 */
  claude: SmartAdjustClaudeConfigSchema.default({}),
});
export type SmartAdjustIntegrations = z.infer<typeof SmartAdjustIntegrationsSchema>;

// ─── 智慧調整專屬 AI 設定(獨立於全域 ai.provider)─────────

/**
 * 我獨自升級用的 AI provider 設定。
 * 跟全域 `ai.provider`(智慧客服 / AI followup 用的)分開,
 * 因為 plan 生成需要強推理模型,通常用 OpenAI GPT-5.5 而非 Gemini。
 *
 * Base URL 一律從「OpenAI 提供商」(`openai.image-provider`)繼承。
 * API Key 優先用此處的 apiKey;若留空,fallback 用「OpenAI 提供商」的 key
 * (OpenAI Codex CLI(此設定的舊執行體)/ 推理模型可能需要不同 OpenAI 帳號 / 不同 billing 的 key,故開放本地覆寫)。
 *
 * 未啟用 / 模型未填 → fallback 全域 ai.provider。
 */
export const SmartAdjustAiConfigSchema = z.object({
  /** OpenAI 相容 model 名稱,例:gpt-5.5 / gpt-5 / o3 */
  model: z.string().trim().max(100).default(''),
  /**
   * 本地 API key(覆寫「OpenAI 提供商」的 key)。
   * 寫入時空字串 = 保留 DB 內舊值(避免不小心洗掉);
   * 真要清空,請改傳專屬 sentinel `"<<clear>>"` 或在 UI 另外做按鈕。
   * GET 時 mask 成 apiKeyConfigured boolean。
   */
  apiKey: z.string().max(500).default(''),
  /** 啟用此專屬設定(false 時 fallback 用全域 ai.provider)*/
  enabled: z.boolean().default(false),
});
export type SmartAdjustAiConfig = z.infer<typeof SmartAdjustAiConfigSchema>;

export const SmartAdjustAiConfigViewSchema = z.object({
  model: z.string(),
  enabled: z.boolean(),
  /** 本地是否已設定 apiKey(設了 = 覆寫繼承)*/
  apiKeyConfigured: z.boolean(),
  /** 從「OpenAI 提供商」讀到的 base URL(僅顯示用)*/
  inheritedBaseUrl: z.string(),
  /** OpenAI 提供商是否已設好 apiKey(用來顯示是否能正常運作)*/
  inheritedApiKeyConfigured: z.boolean(),
  /** OpenAI 提供商是否 enabled(getResolved 會檢查)*/
  inheritedProviderEnabled: z.boolean(),
});
export type SmartAdjustAiConfigView = z.infer<typeof SmartAdjustAiConfigViewSchema>;

/** GET 回傳:apiKey / accessToken 會被 mask;PUT 接收原值 */
export const SmartAdjustIntegrationsViewSchema = z.object({
  redmine: z.object({
    url: z.string(),
    projectIdentifier: z.string(),
    apiKeyConfigured: z.boolean(),
  }),
  gitlab: z.object({
    url: z.string(),
    accessTokenConfigured: z.boolean(),
  }),
  /** 不含機密,GET 直接回原值 */
  claude: SmartAdjustClaudeConfigSchema,
});
export type SmartAdjustIntegrationsView = z.infer<typeof SmartAdjustIntegrationsViewSchema>;

/**
 * 主機端環境明細(REQ-20260826001),對應環境檢查彈窗的四個項目。
 * 全部來自主機端一次 SSH 探測,不發任何 Claude API 請求。
 */
export const SmartAdjustClaudeEnvSchema = z.object({
  /** 主機 CPU 架構(mac mini 是 arm64) */
  arch: z.string().nullable(),
  node: z.object({
    ok: z.boolean(),
    version: z.string().nullable(),
    detail: z.string(),
  }),
  cli: z.object({
    ok: z.boolean(),
    /** 目前安裝的版本,例:`2.1.220 (Claude Code)` */
    version: z.string().nullable(),
    /** npm registry 上的最新版;查不到是 null(不當成錯誤) */
    latest: z.string().nullable(),
    updateAvailable: z.boolean(),
    detail: z.string(),
  }),
  auth: z.object({
    loggedIn: z.boolean(),
    authMethod: z.string().nullable().optional(),
    /** `claude auth status --text` 的第一行,例:`Login: Expired — log in again` */
    detail: z.string(),
  }),
  /** 主機上是否有 claude 行程在跑(有的話不給更新版本) */
  busy: z.boolean(),
});
export type SmartAdjustClaudeEnv = z.infer<typeof SmartAdjustClaudeEnvSchema>;

/**
 * 「主機上的 Claude Code」橋接狀態,給頁面的指示燈用。
 * 是實際 SSH 探測的結果,不是「設定檔有填」——
 * mac mini 重開機後沒人登入時,設定都在但服務是不會恢復的。
 */
export const SmartAdjustClaudeStatusSchema = z.object({
  ok: z.boolean(),
  /** 人看得懂的說明;ok=false 時是失敗原因 */
  detail: z.string(),
  /** 這次探測的時間(ISO string) */
  checkedAt: z.string(),
  /**
   * 主機端環境明細(REQ-20260826001)。探測不到主機時是 null。
   *
   * 為什麼要把登入狀態帶進來:舊版指示燈只驗「SSH 通 + claude 執行檔在」,
   * 刻意不碰憑證 —— 結果 2026-08-26 訂閱憑證過期那次全程綠燈,使用者要等到
   * 跑完分析才看到「OAuth session expired」。會騙人的燈比沒有燈更糟。
   */
  env: SmartAdjustClaudeEnvSchema.nullable().optional(),
});
export type SmartAdjustClaudeStatus = z.infer<typeof SmartAdjustClaudeStatusSchema>;

// ─── 登入流程(REQ-20260826001)────────────────────────────

/**
 * 啟動登入的結果。
 *
 * `url` 是 Claude 的授權網址。redirect_uri 走 platform.claude.com,
 * **不是 localhost** —— 所以授權可以在任何一台機器的瀏覽器完成,不必人在
 * mac mini 前面;授權後網頁會給一段 code,再送回 submit 即可。
 */
export const SmartAdjustClaudeLoginStartSchema = z.object({
  ok: z.boolean(),
  url: z.string().optional(),
  /** 主機端這個等待中的 session 會活多久(秒) */
  expiresInSec: z.number().optional(),
  error: z.string().optional(),
});
export type SmartAdjustClaudeLoginStart = z.infer<typeof SmartAdjustClaudeLoginStartSchema>;

export const SmartAdjustClaudeLoginSubmitInputSchema = z.object({
  /** 授權頁給的 code(`<code>#<state>` 形式) */
  code: z.string().min(1).max(512),
});
export type SmartAdjustClaudeLoginSubmitInput = z.infer<
  typeof SmartAdjustClaudeLoginSubmitInputSchema
>;

export const SmartAdjustClaudeLoginSubmitSchema = z.object({
  ok: z.boolean(),
  loggedIn: z.boolean().optional(),
  error: z.string().optional(),
  /** 失敗時主機端的原始輸出尾段,給工程用 */
  detail: z.string().optional(),
});
export type SmartAdjustClaudeLoginSubmit = z.infer<typeof SmartAdjustClaudeLoginSubmitSchema>;

/** 更新主機端 Claude Code 的結果(REQ-20260826001)。 */
export const SmartAdjustClaudeUpdateSchema = z.object({
  ok: z.boolean(),
  /** 更新前的版本字串 */
  before: z.string().nullable().optional(),
  /** 更新後的版本字串 */
  version: z.string().nullable().optional(),
  error: z.string().optional(),
  detail: z.string().optional(),
});
export type SmartAdjustClaudeUpdate = z.infer<typeof SmartAdjustClaudeUpdateSchema>;

// ─── 用量查詢(REQ-20260802002)────────────────────────────

/**
 * 訂閱額度狀態,原封不動來自 Claude Code 串流裡的 `rate_limit_event`。
 *
 * ⚠️ **拿不到「還剩幾 %」** —— Claude Code 不提供那個數字。能回答的只有
 * 「現在還能不能跑」(status)與「幾點重置」(resetsAt)。UI 不要暗示它是用量條。
 *
 * ⚠️ **也拿不到「總體 / 每週用量」** —— 實測(2026-08-02)整趟串流只會送出**一筆**
 * `rate_limit_event`,`rateLimitType` 固定是 `five_hour`(五小時滾動視窗)。
 * 沒有 headless 的 `claude usage` 子命令(`/usage` 只存在互動 session),
 * `~/.claude/` 也沒有本地用量檔。所以這裡永遠只會有五小時視窗這一種。
 */
export const SmartAdjustQuotaInfoSchema = z.object({
  /** 'allowed' = 正常;其他值(如 'rejected')= 受限中 */
  status: z.string(),
  /** 額度視窗種類,例:'five_hour' */
  rateLimitType: z.string().nullable(),
  /** 重置時間(unix 秒)*/
  resetsAt: z.number().int().nullable(),
  /** 超額方案是否啟用中 */
  isUsingOverage: z.boolean().nullable(),
});
export type SmartAdjustQuotaInfo = z.infer<typeof SmartAdjustQuotaInfoSchema>;

export const SmartAdjustQuotaSchema = z.object({
  /** 有沒有拿到額度資訊(拿不到不代表額度有問題,可能只是這次沒送事件)*/
  ok: z.boolean(),
  info: SmartAdjustQuotaInfoSchema.nullable(),
  /** 這筆資訊是什麼時候觀察到的(ISO string)*/
  observedAt: z.string().nullable(),
  /**
   * 來源:'task' = 順便從最近一次分析 / 升級的串流撈到(免費);
   * 'probe' = 使用者按「檢查額度」主動查(會消耗少量額度)
   */
  source: z.enum(['task', 'probe']).nullable(),
  /** ok=false 時的原因 */
  detail: z.string(),
  /**
   * 「詳細情形」用的原始回傳(REQ-20260802004)。
   *
   * 我們自己整理過的欄位一定是有損的 —— 例如 `overageStatus` /
   * `overageDisabledReason` 一開始就沒接、`usage` 裡的 cache 拆分也被加總掉了。
   * 原樣留一份,UI 展開就能看到 Claude Code 到底回了什麼,不必再翻 log。
   */
  details: z
    .object({
      /** 原始 `rate_limit_info` 全欄位 */
      rateLimit: z.record(z.unknown()).nullable(),
      /** 原始 `result.usage` 全欄位(含 cache_creation / cache_read 拆分)*/
      usage: z.record(z.unknown()).nullable(),
      costUsd: z.number().nullable(),
      /** 這次是哪個模型回報的 */
      model: z.string().nullable(),
    })
    .nullable(),
});
export type SmartAdjustQuota = z.infer<typeof SmartAdjustQuotaSchema>;

/**
 * 每日用量彙總。
 *
 * `costUsd` 是 Claude Code 自己回報的 `total_cost_usd` —— 訂閱方案下它是
 * 「同樣的用量換算成 API 價格會是多少」,**不是實際帳單金額**(訂閱是固定月費)。
 * 拿它當「這次任務有多重」的相對指標,不要當成應付款。
 */
export const SmartAdjustUsageDaySchema = z.object({
  /** 台北時區的日期 yyyy-mm-dd */
  date: z.string(),
  /** 當日任務筆數 */
  tasks: z.number().int(),
  analyzeTokensIn: z.number().int(),
  analyzeTokensOut: z.number().int(),
  codeTokensIn: z.number().int(),
  codeTokensOut: z.number().int(),
  costUsd: z.number(),
});
export type SmartAdjustUsageDay = z.infer<typeof SmartAdjustUsageDaySchema>;

export const SmartAdjustUsageSchema = z.object({
  /** 統計區間天數 */
  days: z.number().int(),
  /** 由新到舊,只含有資料的日子 */
  daily: z.array(SmartAdjustUsageDaySchema),
  /** 區間合計 */
  total: SmartAdjustUsageDaySchema.omit({ date: true }),
  /**
   * 區間內完全沒有任何用量記錄的筆數(三段都空)。
   * 不揭露的話,舊資料會讓合計看起來偏低而沒人知道為什麼。
   */
  missingUsage: z.number().int(),
  /**
   * 區間內沒有成本 / 改 code token 記錄的筆數。
   *
   * 跟 `missingUsage` 分開的理由:`costUsd` 與 `codeTokens*` 是 REQ-20260802002
   * 才加的欄位,**之前的任務一筆都沒有**。只報 missingUsage 的話,畫面會出現
   * 「整個成本欄都是 —,提示卻說只有 1 筆沒記錄」這種自相矛盾的組合,
   * 讓人以為其他任務的成本真的是 0。
   */
  missingCost: z.number().int(),
});
export type SmartAdjustUsage = z.infer<typeof SmartAdjustUsageSchema>;

// ─── 主機 Claude Code 的總體用量(REQ-20260802005)──────────

export const SmartAdjustLocalUsageBucketSchema = z.object({
  calls: z.number().int(),
  /** 未被快取的輸入 */
  inputTokens: z.number().int(),
  cacheCreationTokens: z.number().int(),
  cacheReadTokens: z.number().int(),
  outputTokens: z.number().int(),
  /** 三種輸入相加 = 模型真正讀進去的量 */
  totalInputTokens: z.number().int(),
});
export type SmartAdjustLocalUsageBucket = z.infer<typeof SmartAdjustLocalUsageBucketSchema>;

/**
 * 「當前 5 小時 session 區塊」的用量。Claude Code 的額度是 5 小時滾動視窗,
 * 所以真正該盯的是這一格,而不是「近 30 天總量」。
 *
 * 區塊切法與 ccusage / Claude-Code-Usage-Monitor 一致:第一筆呼叫往下取整到整點
 * 為起點,長度 5 小時;閒置滿 5 小時也開新區塊。實測本地推算的區塊結束時間與
 * 官方 `resetsAt` 只差 10 分鐘,可互相印證。
 */
export const SmartAdjustLocalBlockSchema = z.object({
  start: z.string(),
  end: z.string(),
  messages: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  totalTokens: z.number().int(),
  /** model → 該區塊內的 token 數 */
  byModel: z.record(z.number()),
  elapsedMinutes: z.number().int().optional(),
  remainingMinutes: z.number().int().optional(),
});
export type SmartAdjustLocalBlock = z.infer<typeof SmartAdjustLocalBlockSchema>;

export const SmartAdjustLocalBlockStatsSchema = z.object({
  /** 現在所處的區塊;剛好沒有活動時為 null */
  current: SmartAdjustLocalBlockSchema.nullable(),
  /** 已完成的區塊數 = 推估基準的樣本數。太少時參考線不可靠,UI 要標出來 */
  completedCount: z.number().int(),
  /**
   * ⚠️ 這些「上限」是**用自己的歷史用量推估**的參考線(P90 / 歷史最大),
   * **不是 Anthropic 官方上限** —— 官方從未公開額度換算方式。
   * 參考工具(Claude-Code-Usage-Monitor)採同樣做法。
   */
  p90Tokens: z.number(),
  maxTokens: z.number(),
  p90Messages: z.number(),
  maxMessages: z.number(),
  /** 近 60 分鐘的燃燒速率(token/分)。用滾動視窗,不被區塊開頭的閒置稀釋 */
  burnTokensPerMin: z.number(),
});
export type SmartAdjustLocalBlockStats = z.infer<typeof SmartAdjustLocalBlockStatsSchema>;

/**
 * 由主機上 Claude Code 的 session transcript(`~/.claude/projects/**\/*.jsonl`)
 * 離線加總而來。做法參考 Claude-Code-Usage-Monitor / ccusage。
 *
 * 這是 `rate_limit_event` 給不了的那一半:**總量**。兩者互補 ——
 * 額度事件回答「現在能不能跑」,這裡回答「到底用了多少」。
 *
 * ⚠️ **本機估算,不是 Anthropic 官方的額度消耗**:官方怎麼把 token 換算成額度
 * 並未公開,不能拿這裡的數字反推「還剩多少」。
 *
 * ⚠️ 範圍是主機使用者 `ep` 的**所有** Claude Code 活動(升級任務、維運手動跑的、
 * 任何在該帳號下開的 session)。`project` 是其中屬於本 repo 的部分。
 */
export const SmartAdjustLocalUsageSchema = z.object({
  ok: z.boolean(),
  /** ok=false 時的原因 */
  error: z.string().nullable(),
  generatedAt: z.string().nullable(),
  /** 去重後的呼叫筆數 */
  records: z.number().int(),
  /** transcript 涵蓋的時間範圍 —— 早於這個時間的用量查不到 */
  oldest: z.string().nullable(),
  newest: z.string().nullable(),
  /** 全部活動,依區間 */
  windows: z.record(SmartAdjustLocalUsageBucketSchema),
  /** 其中屬於 interview-system 這個 repo 的部分 */
  project: z.record(SmartAdjustLocalUsageBucketSchema),
  /** 近 30 天依模型 */
  byModel: z.record(SmartAdjustLocalUsageBucketSchema),
  /** 5 小時 session 區塊分析(REQ-20260802006)*/
  block: SmartAdjustLocalBlockStatsSchema.nullable(),
});
export type SmartAdjustLocalUsage = z.infer<typeof SmartAdjustLocalUsageSchema>;

// ─── 官方 /usage 對帳(REQ-20260805001)──────────────────────

/**
 * 主機 Claude Code 的**官方**額度面板。
 *
 * 來源是 `claude -p "/usage"` 的原始輸出 —— 實測 print mode 會直接把官方面板
 * 印成純文字,而且**不產生任何 API 呼叫**(transcript 沒有新記錄)→ 零額度成本。
 * REQ-20260802004/005 當時的結論「`/usage` 只存在於互動 session」已被推翻。
 *
 * 這是本頁唯一的**官方**數字:`SmartAdjustLocalUsage` 是我們自己加總 transcript
 * 的估算,分母還是用 P90 推的參考線;這裡的百分比則是官方自己算的。
 *
 * 注意官方面板自己也標明「Approximate, based on local sessions on this machine」
 * —— 它跟我們讀的是**同一批** transcript,只是換算公式不同(未公開)。所以兩邊
 * 對不上是換算差異,不是有一邊在說謊。
 */
export const SmartAdjustOfficialUsageSchema = z.object({
  ok: z.boolean(),
  /** ok=false 時的原因 */
  error: z.string().nullable(),
  /** 查詢時間(ISO string) */
  fetchedAt: z.string().nullable(),
  /** `claude -p "/usage"` 的**原文**。UI 照登不美化 —— 官方改版時原文永遠是對的 */
  text: z.string(),
  /**
   * 從原文寬鬆解析出來的重點,只為了跟本地估算並排比較。
   * 官方一改格式就會是 null(或個別欄位 null),但 `text` 仍然完整 ——
   * 解析失敗不該讓整個功能壞掉。
   */
  parsed: z
    .object({
      /** Current session: N% used */
      sessionPct: z.number().nullable(),
      /** 原文的重置描述,例:`Aug 5 at 3:50pm (Asia/Taipei)` */
      sessionResets: z.string().nullable(),
      /** Current week (all models): N% used */
      weekPct: z.number().nullable(),
      weekResets: z.string().nullable(),
    })
    .nullable(),
});
export type SmartAdjustOfficialUsage = z.infer<typeof SmartAdjustOfficialUsageSchema>;

export const SmartAdjustSnapshotSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  tier: z.enum(SMART_ADJUST_TIERS),
  request: z.string(),
  plan: z.unknown(), // 前端不嚴格驗證(server-canonical)
  status: z.enum(SMART_ADJUST_STATUSES),
  dbBackupKey: z.string().nullable(),
  gitBeforeSha: z.string().nullable(),
  gitAfterSha: z.string().nullable(),
  summary: z.string(),
  errorMessage: z.string().nullable(),
  tokensIn: z.number().int().nullable(),
  tokensOut: z.number().int().nullable(),
  /** C 級改 code 階段的 token(A/B 永遠 null)*/
  codeTokensIn: z.number().int().nullable().optional(),
  codeTokensOut: z.number().int().nullable().optional(),
  /** 本筆任務總成本 USD(分析 + 改 code);舊資料為 null */
  costUsd: z.number().nullable().optional(),
  createdById: z.number().int().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  appliedAt: z.string().nullable(),
  rolledbackAt: z.string().nullable(),
  /** B 級 dry-run 預覽(plan 階段才有,apply 後 null)*/
  bDryRun: SmartAdjustBDryRunResultSchema.nullable().optional(),
  /** C 級 AI-agent diff(apply 後才有,plan 階段 null;C 級分析時 AI-agent dryRun 也可能填)*/
  cDiff: z.string().nullable().optional(),
  /** GPT 產的白話說明,給非工程使用者看 */
  userExplanation: z.string().nullable().optional(),
  /** GPT 評估的還原難度,給 user 在按「開始升級」前看 */
  restoreComplexity: z.enum(SMART_ADJUST_RESTORE_COMPLEXITIES).nullable().optional(),
  /** 是否涉及 schema 變動(only C 級),true 時 UI 顯示警告 */
  involvesSchemaChange: z.boolean().nullable().optional(),
  /** 當前處於哪一階段(進度條用)*/
  phase: z.enum(SMART_ADJUST_PHASES).nullable().optional(),
  /** 最近一次活動時間(ISO string),前端可推「已停滯多久」*/
  lastHeartbeatAt: z.string().nullable().optional(),
  /** Milestone:mysqldump 備份完成時間(ISO string;non-null = 備份成功) */
  backupAt: z.string().nullable().optional(),
  /** Milestone:C 級 git push 完成時間(ISO string;A/B 永遠 null) */
  pushedAt: z.string().nullable().optional(),
  /** Redmine 追蹤工單 ID(admin 開單後手動回填;UI 從 integrations.redmine.url 拼出連結) */
  redmineIssueId: z.string().nullable().optional(),
});
export type SmartAdjustSnapshot = z.infer<typeof SmartAdjustSnapshotSchema>;

export const SmartAdjustSnapshotListSchema = z.object({
  items: z.array(SmartAdjustSnapshotSchema),
});
export type SmartAdjustSnapshotList = z.infer<typeof SmartAdjustSnapshotListSchema>;
